import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   Container,
   Paper,
   Grid,
   Button,
   CircularProgress,
   Alert,
   Card,
   CardContent,
   CardMedia,
   Chip,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Divider,
   Stack,
   Avatar,
   IconButton,
   Snackbar,
} from '@mui/material';
import {
   Event as EventIcon,
   Place as PlaceIcon,
   DateRange as DateRangeIcon,
   People as PeopleIcon,
   Share as ShareIcon,
   Close as CloseIcon,
   PersonAdd as PersonAddIcon,
   Person as PersonIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { Event } from '../../types/Event';
import { format } from 'date-fns';
import { generateFingerprint } from '../../utils/browserFingerprint';
import { useUser } from '../../contexts/UserContext';
import EventSignupModal from '../../components/auth/EventSignupModal';

const PublicEventPage: React.FC = () => {
   const { eventId } = useParams<{ eventId: string }>();
   const navigate = useNavigate();
   const { currentUser } = useUser();

   const [event, setEvent] = useState<Event | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [rsvpModalOpen, setRsvpModalOpen] = useState(false);
   const [signupModalOpen, setSignupModalOpen] = useState(false);
   const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [name, setName] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>(
      'success'
   );
   const [viewRecorded, setViewRecorded] = useState(false);

   useEffect(() => {
      const fetchEvent = async () => {
         if (!eventId) return;

         try {
            setLoading(true);
            console.log(`Fetching public event with ID: ${eventId}`);

            // Use backend API to fetch public event
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/public/${eventId}`
            );

            if (!response.ok) {
               const errorData = await response.json();
               console.error('API Error:', errorData);
               setError(errorData.error || 'Event not found');
               return;
            }

            const data = await response.json();
            console.log('Event data received:', data);

            if (!data.success || !data.event) {
               setError('Event not found');
               return;
            }

            setEvent(data.event as Event);
         } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event details');
         } finally {
            setLoading(false);
         }
      };

      fetchEvent();
   }, [eventId]);

   // Record event view
   useEffect(() => {
      const recordView = async () => {
         if (!eventId || !event) return;

         try {
            // Determine source from URL params
            const urlParams = new URLSearchParams(window.location.search);
            const source = urlParams.get('source') || 'shareable_link'; // Can be 'qr_scan' or 'shareable_link'

            // Check if view was already recorded in this session for this event+source
            // Using sessionStorage (like business card tracking) to prevent duplicate tracking on refresh
            const sessionKey = `event_view_recorded_${eventId}_${source}`;
            const alreadyRecordedInSession = sessionStorage.getItem(sessionKey);

            if (!viewRecorded && !alreadyRecordedInSession) {
               // Generate fingerprint for tracking
               const fingerprint = await generateFingerprint(true); // true = hardware-only mode
               console.log('Recording event view with fingerprint:', fingerprint);
               console.log('Event view source:', source);

               // Prepare viewer info
               const viewerInfo = currentUser
                  ? {
                       viewerId: currentUser.uid,
                       viewerName: currentUser.displayName || 'Unknown User',
                       viewerEmail: currentUser.email || null,
                       viewerPhotoURL: currentUser.photoURL || null,
                       userAgent: navigator.userAgent,
                    }
                  : {
                       viewerId: null,
                       viewerName: 'Anonymous',
                       viewerEmail: null,
                       viewerPhotoURL: null,
                       userAgent: navigator.userAgent,
                    };

               // Record view via API
               const response = await fetch(`/api/events/${eventId}/record-view`, {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                     fingerprint,
                     viewerInfo,
                     source,
                  }),
               });

               if (response.ok) {
                  const result = await response.json();
                  console.log('Event view recorded successfully:', result);
                  setViewRecorded(true);
                  // Mark as recorded in this session to prevent duplicate views on refresh
                  sessionStorage.setItem(sessionKey, 'true');
                  console.log('✅ View recorded and marked in session storage');
               }
            } else if (alreadyRecordedInSession) {
               console.log('ℹ️ View already recorded in this session, skipping duplicate');
            }
         } catch (error) {
            console.error('Error recording event view:', error);
         }
      };

      recordView();
   }, [eventId, event, currentUser, viewRecorded]);

   const handleRSVPRequest = async () => {
      // Check if user is already logged in
      if (currentUser) {
         // User is logged in, send RSVP request directly
         setSubmitting(true);
         try {
            const token = await auth.currentUser?.getIdToken();
            
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/request-rsvp`,
               {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                     'Authorization': `Bearer ${token}`,
                  },
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('RSVP request sent successfully! You will be notified when approved.');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
            } else {
               setSnackbarMessage(result.error || 'Failed to send RSVP request');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (error) {
            console.error('Error sending RSVP request:', error);
            setSnackbarMessage('Failed to send RSVP request. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            setSubmitting(false);
         }
      } else {
         // User is not logged in, open signup modal
         setSignupModalOpen(true);
      }
   };

   const handleSignupSuccess = async (user: any) => {
      try {
         console.log('✅ User signed up successfully:', user);
         console.log('✅ User automatically added to event waitlist by registration endpoint');

         // Record signup through event analytics
         await fetch(`/api/events/${eventId}/record-signup`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               userId: user.uid,
               userInfo: {
                  userName: user.displayName || user.fullName || 'Unknown',
                  userEmail: user.email || null,
                  userPhotoURL: user.photoURL || null,
               },
            }),
         });

         setSnackbarMessage(
            'Account created successfully! Your request to join the event has been submitted and is pending approval.'
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setSignupModalOpen(false);
      } catch (error) {
         console.error('Error recording signup analytics:', error);
         // Still show success since user is registered and on waitlist
         setSnackbarMessage(
            'Account created successfully! Your request to join the event has been submitted and is pending approval.'
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setSignupModalOpen(false);
      }
   };

   const handleRSVPSubmit = async () => {
      if (!email.trim()) {
         setSnackbarMessage('Please enter your email address');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Check if user exists
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email }),
            }
         );

         const result = await response.json();

         if (result.exists) {
            // User exists, show login modal
            setRsvpModalOpen(false);
            setRegistrationModalOpen(true);
         } else {
            // User doesn't exist, show registration modal
            setRsvpModalOpen(false);
            setRegistrationModalOpen(true);
         }
      } catch (err) {
         console.error('Error checking user:', err);
         setSnackbarMessage('Failed to process request. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleRegistration = async () => {
      if (!email.trim() || !password.trim() || !name.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Register user and add to waiting list
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/events/public-rsvp`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               eventId,
               email,
               password,
               name,
               action: 'register',
            }),
         });

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage(
               'Request submitted successfully! You will be notified when your request is approved.'
            );
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setRegistrationModalOpen(false);
         } else {
            setSnackbarMessage(result.error || 'Registration failed');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error registering:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleLogin = async () => {
      if (!email.trim() || !password.trim()) {
         setSnackbarMessage('Please enter your email and password');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Login user and add to waiting list
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/events/public-rsvp`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               eventId,
               email,
               password,
               action: 'login',
            }),
         });

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage(
               'Login successful! You will be notified when your request is approved.'
            );
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setRegistrationModalOpen(false);
         } else {
            setSnackbarMessage(result.error || 'Login failed');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error logging in:', err);
         setSnackbarMessage('Login failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const formatEventDate = (date: any) => {
      if (!date) return 'TBD';
      const eventDate = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return format(eventDate, 'EEEE, MMMM do, yyyy');
   };

   const formatEventTime = (startDate: any, endDate: any) => {
      if (!startDate) return 'Time TBD';

      const start = startDate.seconds ? new Date(startDate.seconds * 1000) : new Date(startDate);
      const startTime = format(start, 'h:mm a');

      if (!endDate) return startTime;

      const end = endDate.seconds ? new Date(endDate.seconds * 1000) : new Date(endDate);
      const endTime = format(end, 'h:mm a');

      return `${startTime} - ${endTime}`;
   };

   const getLocationDisplay = () => {
      if (!event?.location) return 'Location TBD';
      if (typeof event.location === 'string') return event.location;
      if (event.isVirtual) return 'Virtual Event';
      return event.location.name || event.location.address || 'Location TBD';
   };

   if (loading) {
      return (
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               minHeight: '100vh',
            }}
         >
            <CircularProgress />
         </Box>
      );
   }

   if (error || !event) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Alert severity='error' sx={{ textAlign: 'center' }}>
               {error || 'Event not found'}
            </Alert>
         </Container>
      );
   }

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F9F7F4', py: 2 }}>
         <Container maxWidth='md'>
            <Grid container spacing={2}>
               {/* Event Details - Compact Card */}
               <Grid item xs={12}>
                  <Card
                     sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                     }}
                  >
                     {event.coverImageUrl && (
                        <CardMedia
                           component='img'
                           height='200'
                           image={event.coverImageUrl}
                           alt={event.title}
                           sx={{ objectFit: 'cover' }}
                        />
                     )}
                     <CardContent sx={{ p: 3 }}>
                        {/* Event Title and Actions */}
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 2,
                           }}
                        >
                           <Typography
                              variant='h4'
                              component='h1'
                              sx={{
                                 fontWeight: 'bold',
                                 color: '#004369',
                                 flex: 1,
                                 mr: 2,
                              }}
                           >
                              {event.title}
                           </Typography>
                           <Box
                              sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}
                           >
                           <Button
                              variant='contained'
                              size='small'
                              onClick={handleRSVPRequest}
                              disabled={submitting}
                              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                              sx={{
                                 background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                                 '&:hover': {
                                    background:
                                       'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                                 },
                                 px: 2,
                                 py: 0.5,
                                 fontWeight: 600,
                                 fontSize: '0.875rem',
                              }}
                           >
                              {submitting ? 'Sending...' : 'Request RSVP'}
                           </Button>
                              <IconButton
                                 onClick={() => window.print()}
                                 sx={{
                                    color: '#01949A',
                                    '&:hover': { backgroundColor: 'rgba(1, 148, 154, 0.1)' },
                                 }}
                              >
                                 <ShareIcon />
                              </IconButton>
                           </Box>
                        </Box>

                        {/* Event Type Badge */}
                        {event.eventType && (
                           <Chip
                              label={event.eventType}
                              sx={{
                                 mb: 2,
                                 background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                                 color: 'white',
                                 fontWeight: 600,
                              }}
                           />
                        )}

                        {/* Event Info Grid */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                           <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                 <DateRangeIcon sx={{ color: '#01949A', fontSize: 20 }} />
                                 <Typography variant='body1' sx={{ fontWeight: 500 }}>
                                    {formatEventDate(event.startDate)}
                                 </Typography>
                              </Box>
                           </Grid>
                           <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4 }}>
                                 <Typography
                                    variant='body1'
                                    sx={{ fontWeight: 500, color: '#01949A' }}
                                 >
                                    🕒 {formatEventTime(event.startDate, event.endDate)}
                                 </Typography>
                              </Box>
                           </Grid>
                           <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                 <PlaceIcon sx={{ color: '#01949A', fontSize: 20 }} />
                                 <Typography variant='body1' sx={{ fontWeight: 500 }}>
                                    {getLocationDisplay()}
                                 </Typography>
                              </Box>
                           </Grid>
                        </Grid>

                        {/* Description with HTML parsing */}
                        {event.description && (
                           <Box sx={{ mt: 2 }}>
                              <Typography
                                 variant='h6'
                                 sx={{ mb: 2, color: '#004369', fontWeight: 600 }}
                              >
                                 About This Event
                              </Typography>
                              <Box
                                 sx={{
                                    '& h3': {
                                       color: '#004369',
                                       fontSize: '1.1rem',
                                       fontWeight: 600,
                                       mb: 1,
                                    },
                                    '& strong': { color: '#004369', fontWeight: 600 },
                                    '& ol': { pl: 2 },
                                    '& li': { mb: 0.5, color: '#374151' },
                                    '& p': { mb: 1, color: '#374151' },
                                 }}
                                 dangerouslySetInnerHTML={{ __html: event.description }}
                              />
                           </Box>
                        )}
                     </CardContent>
                  </Card>
               </Grid>
            </Grid>
         </Container>

         {/* Modern RSVP Modal */}
         <Dialog
            open={rsvpModalOpen}
            onClose={() => setRsvpModalOpen(false)}
            maxWidth='sm'
            fullWidth
            PaperProps={{
               sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(1, 148, 154, 0.2)' },
            }}
         >
            <DialogTitle
               sx={{
                  background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                  color: 'white',
                  py: 2,
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6' sx={{ color: 'white', fontWeight: 600 }}>
                     Request to Join Event
                  </Typography>
                  <IconButton onClick={() => setRsvpModalOpen(false)} sx={{ color: 'white' }}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
               <Typography variant='body2' sx={{ mb: 3, color: '#6B7280' }}>
                  Enter your email address to request joining this event.
               </Typography>
               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                     },
                  }}
               />
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
               <Button onClick={() => setRsvpModalOpen(false)} sx={{ color: '#6B7280' }}>
                  Cancel
               </Button>
               <Button
                  onClick={handleRSVPSubmit}
                  variant='contained'
                  disabled={submitting}
                  sx={{
                     background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                     },
                     px: 3,
                     py: 1,
                  }}
               >
                  {submitting ? <CircularProgress size={24} /> : 'Continue'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Registration/Login Modal */}
         <Dialog
            open={registrationModalOpen}
            onClose={() => setRegistrationModalOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>Request to Join Event</Typography>
                  <IconButton onClick={() => setRegistrationModalOpen(false)}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your details to request joining this event.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setRegistrationModalOpen(false)}>Cancel</Button>
               <Button onClick={handleRegistration} variant='contained' disabled={submitting}>
                  {submitting ? <CircularProgress size={24} /> : 'Request'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* EventSignupModal */}
         <EventSignupModal
            open={signupModalOpen}
            onClose={() => setSignupModalOpen(false)}
            eventId={eventId!}
            onSignupSuccess={handleSignupSuccess}
         />

         {/* Snackbar */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default PublicEventPage;
